import {
  Button,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  GIFT_CARD_FAQ,
  GIFT_CARD_LOCAL_STORAGE_KEY,
  GIFT_CARD_PREDETERMINED_AMOUNTS,
  GIFT_CARD_RANGE,
} from 'src/constants/gift-cards';
import { ChangeEvent, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { dollarsToCents } from 'src/utils/currency';
import { validateGiftCardAmount } from 'src/services/panacea/gift-cards';
import FaqAccordion from '../FAQAccordion/FaqAccordion';
import LayoutV2 from '../LayoutV2';
import Header from '../LayoutV2/header';
import Back from '../LayoutV2/header/back';
import Logo from '../LayoutV2/header/logo';
import AccountMenu from '../LayoutV2/account-menu';
import Footer from '../Footer';

const GiftCardsSelection: React.FC = () => {
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );
  const theme = useTheme();
  const router = useRouter();
  const [activeGiftCardAmount, setActiveGiftCardAmount] = useState<
    number | null
  >(null);
  const [activeCustomAmount, setActiveCustomAmount] = useState<boolean>(false);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);
  const [disabledButton, setDisabledButton] = useState<boolean>(true);

  const handleContinueToCheckout = () => {
    if (activeGiftCardAmount) {
      localStorage.setItem(
        GIFT_CARD_LOCAL_STORAGE_KEY,
        activeGiftCardAmount.toString(),
      );
      router.push({
        pathname: '/gift-card/checkout',
        query: { amount: activeGiftCardAmount },
      });
    }
  };

  const handleCustomAmount = (event: ChangeEvent<HTMLInputElement>) => {
    const amountInput = event.target.value;
    if (!amountInput) {
      setActiveGiftCardAmount(null);
      return;
    }
    const amountInputInCents = dollarsToCents(Number(amountInput));
    const { isValid } = validateGiftCardAmount(amountInputInCents);

    //rounds to the nearest dollar if user enters a decimal that's not 0.
    const amountInputInDollars = Number(Number(amountInput).toFixed(0));

    setActiveGiftCardAmount(amountInputInDollars);
    //no error message, just disable button if amount is below min
    if (amountInputInDollars < GIFT_CARD_RANGE.min) {
      setDisabledButton(true);
    }
    //only show error message if user exceeds the max range, not if they go below the min
    if (amountInputInDollars > GIFT_CARD_RANGE.max) {
      setGiftCardError(
        `Amount must be between $${GIFT_CARD_RANGE.min} and $${GIFT_CARD_RANGE.max}`,
      );
    }
    //if amount is within range, remove error message and enable button
    if (isValid) {
      setDisabledButton(false);
      setGiftCardError(null);
    }
  };

  const handlePreselectedAmount = (amount: number) => {
    setActiveGiftCardAmount(amount);
    //clean up state from custom amout form
    setActiveCustomAmount(false);
    setDisabledButton(false);
    setGiftCardError(null);
  };

  return (
    <LayoutV2
      header={
        <Header
          leftContent={
            <>
              <Back onClickCallback={() => router.push('/dashboard')} />
              <Logo />
            </>
          }
          rightContent={<AccountMenu />}
        />
      }
      footer={<Footer isShowingCart={false} />}
    >
      <Grid
        container
        rowSpacing={4}
        sx={{
          textAlign: 'center',
          marginTop: isSmallScreen ? '1rem' : 0,
          padding: '0 0.5rem',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Grid>
          <Typography variant="h3">Buy Gift Card</Typography>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
          }}
        >
          {GIFT_CARD_PREDETERMINED_AMOUNTS.map((amount) => {
            const isAmountActive = activeGiftCardAmount === amount;
            return (
              <Grid
                sx={{
                  padding: isSmallScreen ? '1rem 0.5rem' : '0 2rem',
                  cursor: 'pointer',
                }}
              >
                {' '}
                <Paper
                  component="div"
                  onClick={() => handlePreselectedAmount(amount)}
                  variant={isAmountActive ? 'outlined' : 'elevation'}
                  sx={{
                    backgroundColor: isAmountActive
                      ? theme.palette.grey[200]
                      : theme.palette.background.default,
                    width: isSmallScreen ? '5rem' : '10rem',
                    height: '5rem',
                    display: 'flex',
                    padding: '1rem 0',
                    justifyContent: 'center',
                    alignItems: 'center',
                    //add hover state
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[300]}`,
                    },
                  }}
                  key={amount}
                >
                  ${amount}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            cursor: 'pointer',
            paddingTop: isSmallScreen ? '0.25rem' : '0.75rem',
            textDecoration: 'underline',
          }}
          onClick={() => {
            setActiveCustomAmount(!activeCustomAmount);
            setActiveGiftCardAmount(null);
          }}
        >
          Or enter a custom amount
        </Typography>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          {activeCustomAmount ? (
            <>
              <Typography
                sx={{ color: theme.palette.grey[700] }}
                variant="caption"
              >
                Enter an amount between $25 - $500
              </Typography>
              <TextField
                placeholder="Enter gift card amount"
                value={activeGiftCardAmount}
                inputMode="numeric"
                type="number"
                onChange={handleCustomAmount}
                error={!!giftCardError}
                helperText={giftCardError}
                sx={{ width: isSmallScreen ? '17.5rem' : '15rem' }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              />
            </>
          ) : null}
        </Grid>
        <Grid sx={{ width: '75%', textAlign: 'center' }}>
          <Typography>
            {' '}
            After checkout, you will receive an email with the gift card code.
            To deliver the gift card code to your recipient you can either
            forward them the email or print out the email and gift it to them!
          </Typography>
          <Typography sx={{ marginTop: '1rem' }}>
            The gift card can be redeemed on the{'  '}
            <Link
              target="_blank"
              rel="noreferrer noopener"
              href="/gift-card/redeem"
              css={{ color: theme.palette.common.black }}
            >
              gift card redemption page
            </Link>{' '}
            or during checkout.
          </Typography>
        </Grid>

        <Grid sx={{ marginTop: '1rem', width: '20rem' }}>
          {activeGiftCardAmount ? (
            <Button
              fullWidth
              variant="contained"
              onClick={handleContinueToCheckout}
              disabled={!!giftCardError || disabledButton}
            >
              {giftCardError || disabledButton
                ? 'Checkout'
                : `Checkout for $${activeGiftCardAmount}`}
            </Button>
          ) : (
            <Button fullWidth variant="contained" disabled>
              Checkout
            </Button>
          )}
        </Grid>
        <Grid sx={{ width: '75%', textAlign: 'center' }}>
          <Typography variant={'body2'}>
            By completing this purchase of a gift card, I agree to the Mealogic{' '}
            <a href={'https://mealogic.com/terms-of-use'} target={'_blank'}>
              Terms
            </a>{' '}
            and{' '}
            <a href={'https://mealogic.com/privacy-policy'} target={'_blank'}>
              Privacy Policy
            </a>
            .
          </Typography>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" sx={{ margin: '2rem 0' }}>
            Frequently Asked Questions
          </Typography>
          <FaqAccordion faq={GIFT_CARD_FAQ} />
        </Grid>
      </Grid>
    </LayoutV2>
  );
};

export default GiftCardsSelection;
